import { useCallback, useRef, useState } from "react";
import { assistantHistoryContent, type EditorialBlock, type Lang } from "@majourneys/shared";
import {
  aiChatStream,
  isSignInRequired,
  type AiAnswerFormat,
  type AiChatTurn,
  type AiFollowUp,
  type AiMatch,
  type AiResponseType,
  type AiSuggestion,
  type AiCompareLayout,
  type AiPlaceLayout,
  type ConciergeAsk,
  type ConciergeTimes,
  type AiRideLayout,
  type BookingProposal,
  type StayShelf,
  type Budget,
  type FreeRemaining,
  type Itinerary,
} from "../api/ai.api";
import type { RequestSentInfo } from "../lib/proposal-status";

/** Every SUPPORTED_LANGS locale; the API answers in all seven. */
export type ChatLang = Lang;

export type ChatMessage =
  | { kind: "user"; id: string; text: string }
  | {
      kind: "assistant";
      id: string;
      text: string;
      matches: AiMatch[];
      /** When true, render the text only — no card stack. */
      clarify?: boolean;
      /** Renderer shape — see AiResponseType. Defaults to "qa". */
      type?: AiResponseType;
      /** Present iff `type === "itinerary"`. */
      itinerary?: Itinerary;
      /** Present iff `type === "budget"`. */
      budget?: Budget;
      /** Present iff `type === "ride"`. */
      ride?: AiRideLayout;
      /** Present iff `type === "place"`. */
      place?: AiPlaceLayout;
      /** Present iff `type === "compare"`. */
      compare?: AiCompareLayout;
      /** A booking proposed this turn, shown as a card to confirm. */
      bookingProposal?: BookingProposal;
      /** Stays found this turn, shown as a compact list. */
      stays?: StayShelf;
      /**
       * One-tap answers to a follow-up question the assistant asked. Built
       * from real catalog rows, so these outrank the heuristic chips.
       */
      suggestions?: AiSuggestion[];
      /**
       * The agent's narration, carried here as well as on `pending`.
       *
       * The cards land about a second into a seven second turn, and that
       * `matches` frame PROMOTES the pending bubble to an assistant message.
       * Written only onto `pending`, every later progress line was dropped on
       * the floor: the handler looked for a kind that no longer existed and
       * silently did nothing. Measured end to end — three frames reached the
       * browser, three reached `onProgress`, and none reached the screen.
       */
      steps?: string[];
      /**
       * Shape of `text`. "markdown" is the document the phone renders —
       * headings, bullets, bold and `[[slug|Label]]` names — and the renderer
       * must parse it or the reader sees the raw syntax.
       */
      format?: AiAnswerFormat;
      /** The model's own next questions, written for this answer. */
      followUps?: AiFollowUp[];
      /** Voice layer of an editorial answer: header, sections, estimate. */
      editorial?: EditorialBlock;
      /**
       * The server's id for this answer, from the stream's done frame. What a
       * thumbs up or down is filed against; no id, no thumbs.
       */
      turnId?: string;
      /** The guest's question this turn answered, sent back with a vote. */
      query?: string;
      /**
       * True while the answer is still arriving. Absent on every finished turn,
       * including every turn saved before this field existed.
       */
      streaming?: boolean;
      /** The guest pressed Stop: what arrived stays, nothing more will. */
      stopped?: boolean;
      /**
       * Proof that the guest sent the request this turn proposed, through the
       * place's own form (chat kit C11). Set only from that proof: see
       * lib/proposal-status.ts. Never set by the chat itself.
       */
      requestSent?: RequestSentInfo;
      /**
       * A structured question back (chat kit C20/C21): tappable options or a
       * date and party picker. The same question is in `text` too.
       */
      ask?: ConciergeAsk;
      /** The question was not about travel; `ask` carries two travel ones. */
      offTopic?: true;
      /** Open start times for one place on one day (C22). */
      times?: ConciergeTimes;
    }
  /**
   * The turn is running and has not promoted to an assistant message yet.
   *
   * `steps` is the agent's own narration, one line per tool it runs, oldest
   * first. It exists so the six to eight second wait can show WORK rather than
   * a spinner: the API has always sent these frames and the web dropped them,
   * so the phone showed the work and the website did not.
   */
  | { kind: "pending"; id: string; steps?: string[] };

export interface UseAiChatOptions {
  lang?: ChatLang;
  limit?: number;
  /**
   * Seed the conversation with prior messages (e.g. from saved history).
   * Only read on first mount; subsequent changes via `setMessages`.
   */
  initialMessages?: ChatMessage[];
}

export interface UseAiChatState {
  messages: ChatMessage[];
  loading: boolean;
  error: string | null;
  /**
   * Ask a question. With `replaceFrom`, the guest edited an earlier message:
   * the thread is cut at that message (it and everything after it go) and the
   * new text is sent in its place, with only the turns before it as history.
   */
  send: (query: string, opts?: { replaceFrom?: string }) => Promise<void>;
  /**
   * Stop the answer that is arriving. The thread stays: the guest's question
   * and whatever of the answer already landed are kept. The API stops on the
   * aborted request.
   */
  stop: () => void;
  /**
   * Ask the last question again after a failed answer. The guest's message
   * was kept on screen, so it is re-sent in place rather than added twice.
   */
  retry: () => void;
  /**
   * True when the last Try again failed as well. The host then offers a
   * person on WhatsApp (chat kit C28) instead of a third identical button.
   */
  retryFailed: boolean;
  /**
   * Whether a turn was answered in this chat session. Turns loaded from saved
   * history are not: their one-tap asks render locked, never re-sendable.
   */
  isLive: (id: string) => boolean;
  reset: () => void;
  /**
   * Replace the message list. Useful for switching between saved
   * conversations from the history dropdown.
   */
  setMessages: (messages: ChatMessage[]) => void;
  /**
   * The visitor has used up their free questions and must make an account.
   *
   * Set from the server and only from the server: either the allowance header
   * came back at zero, or a send was refused with 403 `signin_required`. There
   * is deliberately no client-side tally and nothing in localStorage, so
   * clearing site data does not hand out another free question.
   */
  gated: boolean;
  /** Latest value of the `x-mj-free-remaining` header. `null` if unseen. */
  freeRemaining: FreeRemaining | null;
  /**
   * The question the gate refused, so the host can put it back in the composer
   * after sign-in. The turn itself is removed from the thread — a question
   * hanging there with no answer under it reads as a failure, not a gate.
   */
  gatedQuery: string | null;
  /** Called once sign-in succeeds: reopens the composer. */
  clearGate: () => void;
}

/**
 * Which answer shape the chat asks for.
 *
 * The web asked for nothing, which is one unstructured paragraph, and that is
 * exactly what it got: a fifteen-line block of prose with the bullets run
 * together. "editorial" is what the phone asks for — a voice header, themed
 * card sections with a line per place, an estimate table and the three advice
 * slots — and the structure comes from those FIELDS rather than from asking
 * the model to write tidier prose, which the API's own notes record failing
 * six times in one day.
 */
const CHAT_FORMAT: AiAnswerFormat = "editorial";

/** What the API hands the model; anything past these is trimmed, not sent. */
const QUERY_CHARS = 1000;
const HISTORY_TURNS = 20;
const TURN_CHARS = 4000;

function genId(): string {
  return Math.random().toString(36).slice(2, 10);
}

export function useAiChat(opts: UseAiChatOptions = {}): UseAiChatState {
  const { lang = "en", limit = 4, initialMessages } = opts;
  const [messages, setMessages] = useState<ChatMessage[]>(
    () => initialMessages ?? [],
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gated, setGated] = useState(false);
  const [freeRemaining, setFreeRemaining] = useState<FreeRemaining | null>(null);
  const [gatedQuery, setGatedQuery] = useState<string | null>(null);
  const [retryFailed, setRetryFailed] = useState(false);
  /** Set by `retry` for the one send it makes. */
  const retryingRef = useRef(false);
  /** Turns asked in this session; everything else came from history. */
  const liveIdsRef = useRef<Set<string>>(new Set());
  const abortRef = useRef<AbortController | null>(null);
  /** The turn in flight, so Stop knows which bubble to settle. */
  const inFlightRef = useRef<string | null>(null);
  /**
   * The thread as last rendered. `send` reads history from here rather than
   * from its closure so an edit-and-resend fired in the same tick as a state
   * change still cuts the thread the guest is looking at.
   */
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const send = useCallback(
    async (raw: string, opts?: { replaceFrom?: string }) => {
      const query = raw.trim();
      const isRetry = retryingRef.current;
      retryingRef.current = false;
      if (!query) return;
      // Nothing gets sent while the gate is up. The composer is already gone,
      // but a hero chip or a deep link can still call send().
      if (gated) {
        setGatedQuery(query);
        return;
      }
      setError(null);
      setRetryFailed(false);

      const userMsg: ChatMessage = { kind: "user", id: genId(), text: query };
      const pending: ChatMessage = { kind: "pending", id: genId() };
      liveIdsRef.current.add(pending.id);

      // The thread this question is asked into. An edit cuts it at the edited
      // message, so neither it nor any answer after it is sent as history.
      const current = messagesRef.current;
      const cutAt = opts?.replaceFrom
        ? current.findIndex((m) => m.id === opts.replaceFrom)
        : -1;
      const base = cutAt >= 0 ? current.slice(0, cutAt) : current;

      // Build history from messages that already exist BEFORE we append the new turn.
      // Only the tail the API reads: sending the whole chat made every long
      // conversation fail with a 422 once it passed the API's limits.
      const history: AiChatTurn[] = base
        .filter((m): m is Extract<ChatMessage, { kind: "user" | "assistant" }> => m.kind !== "pending")
        .slice(-HISTORY_TURNS)
        .map((m) => ({
          role: m.kind === "user" ? "user" : "assistant",
          // A turn that showed a plan sends the plan too: the text alone is
          // often one opening sentence, and an edit needs the plan it edits.
          content:
            m.kind === "assistant"
              ? assistantHistoryContent(m.text, m.itinerary?.days, m.matches, TURN_CHARS)
              : m.text.slice(0, TURN_CHARS),
        }));

      // An edit replaces the thread wholesale; a plain send appends to it.
      setMessages((prev) =>
        cutAt >= 0 ? [...base, userMsg, pending] : [...prev, userMsg, pending],
      );
      setLoading(true);

      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      inFlightRef.current = pending.id;

      try {
        // Swap the pending bubble for a real assistant turn the moment the
        // matches land — retrieval finishes seconds before the model does,
        // so the cards should not wait for the prose.
        const promote = (patch: Partial<Extract<ChatMessage, { kind: "assistant" }>>) => {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === pending.id
                ? ({
                    kind: "assistant",
                    id: m.id,
                    text: "",
                    matches: [],
                    query,
                    streaming: true,
                    // Narration collected while the bubble was still pending.
                    ...(m.kind === "pending" && m.steps ? { steps: m.steps } : {}),
                    ...(m.kind === "assistant" ? m : {}),
                    ...patch,
                  } as ChatMessage)
                : m,
            ),
          );
        };

        const res = await aiChatStream(
          { query: query.slice(0, QUERY_CHARS), history, lang, limit, format: CHAT_FORMAT },
          {
            onMatches: (e) => {
              promote({
                matches: e.matches,
                clarify: e.clarify,
                ...(e.answerType ? { type: e.answerType } : {}),
                ...(e.itinerary ? { itinerary: e.itinerary } : {}),
                ...(e.budget ? { budget: e.budget } : {}),
                ...(e.suggestions ? { suggestions: e.suggestions } : {}),
                ...(e.format ? { format: e.format } : {}),
                ...(e.followUps ? { followUps: e.followUps } : {}),
                ...(e.editorial ? { editorial: e.editorial } : {}),
              });
            },
            onProgress: (text) => {
              setMessages((prev) =>
                prev.map((m) =>
                  // Either kind: the message may already have been promoted by
                  // the matches frame, which arrives long before the prose.
                  m.id === pending.id && m.kind !== "user"
                    ? { ...m, steps: [...(m.steps ?? []), text] }
                    : m,
                ),
              );
            },
            onDelta: (text) => {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === pending.id && m.kind === "assistant"
                    ? { ...m, text: m.text + text }
                    : m,
                ),
              );
            },
          },
          { signal: ctrl.signal },
        );

        if (ctrl.signal.aborted) return;
        // Reconcile with the server's authoritative payload. Also covers the
        // non-streaming fallback, where no events fired at all.
        setMessages((prev) =>
          prev.map((m) =>
            m.id === pending.id
              ? {
                  kind: "assistant",
                  id: m.id,
                  text: res.answer,
                  matches: res.matches,
                  clarify: res.clarify,
                  type: res.type,
                  itinerary: res.itinerary,
                  budget: res.budget,
                  ride: res.ride,
                  place: res.place,
                  compare: res.compare,
                  bookingProposal: res.bookingProposal,
                  stays: res.stays,
                  suggestions: res.suggestions,
                  format: res.format,
                  followUps: res.followUps,
                  editorial: res.editorial,
                  query,
                  // Kept from the stream: the done frame does not repeat it.
                  ...(m.kind !== "user" && m.steps ? { steps: m.steps } : {}),
                  ...(res.turnId ? { turnId: res.turnId } : {}),
                  ...(res.ask ? { ask: res.ask } : {}),
                  ...(res.offTopic ? { offTopic: true as const } : {}),
                  ...(res.times ? { times: res.times } : {}),
                }
              : m,
          ),
        );

        // The answer is already on screen and stays there. All the header
        // decides is whether the composer comes back under it.
        //
        // Except after a clarifying reply. The stream's header is written
        // before the reply exists, so it is the count once an answer lands,
        // but the server does not charge a reply that asks something back
        // ("which city?", or the off-topic refusal). Believing it closed the
        // chat on a first-time visitor who had not had an answer yet.
        const remaining = res.clarify ? null : (res.freeRemaining ?? null);
        if (remaining !== null) {
          setFreeRemaining(remaining);
          setGated(remaining !== "unlimited" && remaining <= 0);
        }
      } catch (err) {
        if (ctrl.signal.aborted) return;

        // Not a failure: the allowance ran out. Drop the turn that was refused
        // — it never got an answer — and keep every earlier turn exactly as it
        // was, because the answer they already read is the reason to sign up.
        if (isSignInRequired(err)) {
          setGated(true);
          setFreeRemaining(0);
          setGatedQuery(query);
          setMessages((prev) =>
            prev.filter((m) => m.id !== pending.id && m.id !== userMsg.id),
          );
          return;
        }

        // The detail is for us, not the traveller: every surface shows the
        // translated sentence, so it goes to the console and nowhere else.
        console.error("[ai-chat]", err);
        setError(err instanceof Error ? err.message : "MaJourneys AI is unavailable");
        setRetryFailed(isRetry);
        setMessages((prev) => prev.filter((m) => m.id !== pending.id));
      } finally {
        if (abortRef.current === ctrl) {
          abortRef.current = null;
          inFlightRef.current = null;
          setLoading(false);
        }
      }
    },
    [lang, limit, gated],
  );

  const stop = useCallback(() => {
    const ctrl = abortRef.current;
    const id = inFlightRef.current;
    if (!ctrl) return;
    ctrl.abort();
    abortRef.current = null;
    inFlightRef.current = null;
    setLoading(false);
    // Settle the turn that was arriving. A bubble that never got a word is
    // removed (a wait indicator left spinning reads as still working); one
    // that did keeps every word and card it had, marked as stopped.
    setMessages((prev) =>
      prev.flatMap((m) => {
        if (m.id !== id) return [m];
        if (m.kind === "pending") return [];
        if (m.kind !== "assistant") return [m];
        if (!m.text.trim() && !m.itinerary && !m.budget) return [];
        const { streaming: _streaming, ...rest } = m;
        return [{ ...rest, stopped: true }];
      }),
    );
  }, []);

  const retry = useCallback(() => {
    const last = [...messagesRef.current].reverse().find((m) => m.kind === "user");
    if (!last || last.kind !== "user") return;
    retryingRef.current = true;
    void send(last.text, { replaceFrom: last.id });
  }, [send]);

  const isLive = useCallback((id: string) => liveIdsRef.current.has(id), []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    inFlightRef.current = null;
    setMessages([]);
    setError(null);
    setRetryFailed(false);
    setLoading(false);
    // `gated` deliberately survives: starting a new chat is not a way to earn
    // another free question.
  }, []);

  const clearGate = useCallback(() => {
    setGated(false);
    // Unknown again rather than optimistic. The next answer's header sets it.
    setFreeRemaining(null);
    setGatedQuery(null);
  }, []);

  return {
    messages,
    loading,
    error,
    send,
    stop,
    retry,
    retryFailed,
    isLive,
    reset,
    setMessages,
    gated,
    freeRemaining,
    gatedQuery,
    clearGate,
  };
}
